import { CUSTOM_ELEMENTS_SCHEMA, Component, computed, inject, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { InstitutoPrevidenciarioService } from '../../../core/services/instituto-previdenciario.service';
import { EmptyStateComponent } from '../../../shared/components/empty-state/empty-state.component';
import { MenuAcoesComponent, OpcaoMenuAcoes } from '../../../shared/components/menu-acoes/menu-acoes.component';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { InstitutoFiltros, InstitutoLista, InstitutoPrevidenciario } from '../../../core/models/instituto-previdenciario.model';
import { formatDateIsoToBr } from '../../../core/utils/date-format.util';
import { Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { FilterBarComponent, FilterField } from '../../../shared/components/filter-bar/filter-bar.component';
import { ToastService } from '../../../core/services/toast.service';
import { ApiHttpError } from '../../../shared/services/http-client/api-http-error.model';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import {
  INSTITUTO_PLANOS,
  INSTITUTO_STATUS,
  INSTITUTO_TIPOS,
  InstitutoOpcao,
  getInstitutoOpcaoLabel,
  getInstitutoStatusCor,
  getInstitutoStatusOpcao,
  normalizarStatus,
} from '../../../core/constants/instituto-previdenciario-opcoes';
import { StatusPieChartComponent, StatusPieChartSegment } from '../../../shared/components/status-pie-chart/status-pie-chart.component';
import { InstitutoStatusTagComponent } from '../../../shared/components/instituto-status-tag/instituto-status-tag.component';

/** Ações do menu ⋮ de cada linha da tabela. */
const OPCOES_MENU: readonly OpcaoMenuAcoes[] = [{ id: 'visualizar', rotulo: 'Visualizar', icone: 'eye' }];

/** Tamanho inicial da página; o rodapé da tabela troca para 50 ou 100. */
const PAGE_SIZE = 10;

/** Status que não entram no gráfico "Adesões por status" (continuam na tabela e no filtro). */
const STATUS_FORA_DO_GRAFICO = new Set(['Pendente'].map(normalizarStatus));

/** Se o status (comparado sem maiúsculas nem acentos) fica fora do gráfico. */
const foraDoGrafico = (status: string): boolean => STATUS_FORA_DO_GRAFICO.has(normalizarStatus(status));

/** Totais antes da primeira carga e depois de um erro. */
const TOTAIS_VAZIOS: Omit<InstitutoLista, 'solicitacoes'> = {
  totalAdesoes: 0,
  qtdPorAutopatrocinio: 0,
  qtdPorBpd: 0,
  qtdPorResgate: 0,
  qtdPorPortabilidade: 0,
};

/** Colunas da tabela que podem ser ordenadas pelo cabeçalho. */
export type ColunaOrdenavel = 'nome' | 'matricula' | 'tipoInstituto' | 'dataSolicitacao' | 'status';
type DirecaoOrdenacao = 'asc' | 'desc';
interface Ordenacao {
  coluna: ColunaOrdenavel;
  direcao: DirecaoOrdenacao;
}

const compararTexto = (a: string, b: string): number =>
  a.localeCompare(b, 'pt-BR', { sensitivity: 'base', numeric: true });

/**
 * Comparação crescente da coluna: texto em ordem alfabética (sem diferenciar maiúsculas e
 * acentos), matrícula numérica e data pela ISO `yyyy-MM-dd`. Sem data fica por último.
 */
function compararPorColuna(a: InstitutoPrevidenciario, b: InstitutoPrevidenciario, coluna: ColunaOrdenavel): number {
  switch (coluna) {
    case 'nome':
      return compararTexto(a.nomeParticipante, b.nomeParticipante);
    case 'matricula':
      return a.matricula - b.matricula;
    case 'tipoInstituto':
      return compararTexto(a.tipoInstituto, b.tipoInstituto);
    case 'status':
      return compararTexto(a.statusUnificado, b.statusUnificado);
    case 'dataSolicitacao':
      if (!a.dataSolicitacao || !b.dataSolicitacao) {
        return Number(!a.dataSolicitacao) - Number(!b.dataSolicitacao);
      }
      return a.dataSolicitacao.localeCompare(b.dataSolicitacao);
  }
}

/** Opções de select a partir das listas locais: o `id` vai como valor do filtro. */
function toFilterOptions(opcoes: readonly InstitutoOpcao[]): { value: string; label: string }[] {
  return [
    { value: 'todos', label: 'Todos' },
    ...opcoes.map((opcao) => ({ value: String(opcao.id), label: opcao.label })),
  ];
}

@Component({
  selector: 'app-instituto-previdenciario',
  imports: [
    RouterLink,
    NgTemplateOutlet,
    EmptyStateComponent,
    FilterBarComponent,
    MenuAcoesComponent,
    PaginationComponent,
    StatusPieChartComponent,
    InstitutoStatusTagComponent,
  ],
  templateUrl: './instituto-previdenciario.component.html',
  styleUrl: './instituto-previdenciario.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
/** A carga inicial vem do `app-filter-bar`, que emite os filtros padrão ao iniciar. */
export class InstitutoPrevidenciarioComponent {
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly institutoPrevidenciarioService = inject(InstitutoPrevidenciarioService);
  private readonly solicitacoesSignal = signal<readonly InstitutoPrevidenciario[]>([]);
  readonly solicitacoes = this.solicitacoesSignal.asReadonly();
  readonly formatDate = formatDateIsoToBr;
  readonly currentPage = signal(1);
  readonly pageSize = signal(PAGE_SIZE);
  readonly opcoesMenu = OPCOES_MENU;
  /** Coluna e direção da ordenação da tabela; `null` mantém a ordem da API. */
  readonly ordenacao = signal<Ordenacao | null>(null);
  /** A API devolve todas as solicitações: ordenação e paginação são feitas aqui, nessa ordem. */
  readonly solicitacoesOrdenadas = computed(() => {
    const ordenacao = this.ordenacao();
    const lista = this.solicitacoesSignal();
    if (!ordenacao) {
      return lista;
    }

    const fator = ordenacao.direcao === 'asc' ? 1 : -1;
    return [...lista].sort((a, b) => fator * compararPorColuna(a, b, ordenacao.coluna));
  });
  readonly solicitacoesPaginadas = computed(() => {
    const inicio = (this.currentPage() - 1) * this.pageSize();
    return this.solicitacoesOrdenadas().slice(inicio, inicio + this.pageSize());
  });
  /** Totais da API: não mudam com os filtros, que valem só para a tabela e o gráfico de status. */
  private readonly totaisSignal = signal<Omit<InstitutoLista, 'solicitacoes'>>(TOTAIS_VAZIOS);
  readonly adesoesTotalLabel = computed(() => this.totaisSignal().totalAdesoes.toLocaleString('pt-BR'));
  readonly autopatrocinioTotal = computed(() => this.totaisSignal().qtdPorAutopatrocinio);
  readonly bpdTotal = computed(() => this.totaisSignal().qtdPorBpd);
  readonly resgateTotal = computed(() => this.totaisSignal().qtdPorResgate);
  readonly portabilidadeTotal = computed(() => this.totaisSignal().qtdPorPortabilidade);
  /**
   * Painel do topo (mesmo layout do Requerimento Online): total de adesões e, por tipo de instituto,
   * a quantidade e o percentual sobre o total, na barra em linha. Portabilidade fica oculta por
   * enquanto e volta para a lista quando o indicador for exibido (o total já vem em `portabilidadeTotal`).
   */
  readonly indicadores = computed(() => {
    const total = this.totaisSignal().totalAdesoes;
    const tipos: StatusPieChartSegment[] = [
      { label: 'Autopatrocínio', value: this.autopatrocinioTotal(), color: 'var(--color-primary-v500)' },
      { label: 'BPD', value: this.bpdTotal(), color: 'var(--color-green-v500)' },
      { label: 'Resgate', value: this.resgateTotal(), color: 'var(--color-red-v500)' }
    ];
    return {
      total: this.adesoesTotalLabel(),
      tipos: tipos.map((tipo) => ({
        ...tipo,
        valorTexto: tipo.value.toLocaleString('pt-BR'),
        percentual: total ? Math.round((tipo.value / total) * 100) : 0,
      })),
    };
  });
  readonly planoLabel =(id: number | null | undefined) => getInstitutoOpcaoLabel(INSTITUTO_PLANOS, id) ?? '-';
  readonly adesoesPorInstituto = computed<readonly StatusPieChartSegment[]>(() => [
    { label: 'Autopatrocínio', value: this.autopatrocinioTotal(), color: 'var(--color-primary-v500)' },
    { label: 'Benefício Proporcional Diferido (BPD)', value: this.bpdTotal(), color: 'var(--color-green-v500)' },
    { label: 'Resgate', value: this.resgateTotal(), color: 'var(--color-red-v500)' }
  ]);
  /**
   * Conta o `statusUnificado` de cada solicitação (o mesmo texto da tabela). Uma fatia por status
   * de `INSTITUTO_STATUS`, na ordem da lista e mesmo com total zero, cada um com cor fixa; o texto
   * casa com a opção pelo código da API ou pela label, sem diferenciar maiúsculas nem acentos
   * ("EmRetencao" e "Concluido" contam em "Em retenção" e "Concluído"). Status fora da lista
   * entram no fim; vazio não entra. A cor vem de `getInstitutoStatusCor` (cinza para status sem
   * cor definida).
   */
  readonly adesoesPorStatus = computed<readonly StatusPieChartSegment[]>(() => {
    const totais = new Map<string, number>(
      INSTITUTO_STATUS.filter((opcao) => !foraDoGrafico(opcao.label)).map((opcao) => [opcao.label, 0]),
    );
    for (const { statusUnificado } of this.solicitacoesSignal()) {
      const label = getInstitutoStatusOpcao(statusUnificado)?.label ?? statusUnificado.trim();
      if (label && !foraDoGrafico(label)) {
        totais.set(label, (totais.get(label) ?? 0) + 1);
      }
    }

    return [...totais].map(([label, value]) => ({ label, value, color: getInstitutoStatusCor(label).cor }));
  });
  readonly filterFields = computed<FilterField[]>(() => [
    {
      key: 'nome',
      label: 'Procure por nome',
      type: 'text',
      placeholder: 'Digite o nome do participante',
    },
    {
      key: 'matricula',
      label: 'Procure por matrícula',
      type: 'text',
      placeholder: 'Digite a matrícula do participante',
    },
    {
      key: 'plano',
      label: 'Plano',
      type: 'select',
      defaultValue: 'todos',
      options: toFilterOptions(INSTITUTO_PLANOS),
    },
    {
      key: 'idTipoSolicitacao',
      label: 'Tipo de Instituto',
      type: 'select',
      defaultValue: 'todos',
      options: toFilterOptions(INSTITUTO_TIPOS),
    },
    {
      key: 'statusUnificado',
      label: 'Status',
      type: 'select',
      defaultValue: 'todos',
      // O status vai para a API pelo código (`valor`, ex.: "EmRetencao"), não pelo id nem pela label.
      options: [
        { value: 'todos', label: 'Todos' },
        ...INSTITUTO_STATUS.map((opcao) => ({ value: opcao.valor, label: opcao.label })),
      ],
    },
    {
      key: 'periodoInicial',
      label: 'Período',
      type: 'daterange',
      keyTo: 'periodoFinal',
    },
  ]);

  /** Nova quantidade por página volta para a primeira página. */
  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.currentPage.set(1);
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
  }

  /** Primeiro clique ordena a coluna de forma crescente; clicar de novo inverte a direção. */
  ordenarPor(coluna: ColunaOrdenavel): void {
    const atual = this.ordenacao();
    const direcao: DirecaoOrdenacao = atual?.coluna === coluna && atual.direcao === 'asc' ? 'desc' : 'asc';
    this.ordenacao.set({ coluna, direcao });
    this.currentPage.set(1);
  }

  /** Valor de `aria-sort` do cabeçalho da coluna. */
  ariaSort(coluna: ColunaOrdenavel): 'ascending' | 'descending' | 'none' {
    const ordenacao = this.ordenacao();
    if (ordenacao?.coluna !== coluna) {
      return 'none';
    }
    return ordenacao.direcao === 'asc' ? 'ascending' : 'descending';
  }

  /** Seta para cima na coluna sem ordenação (esmaecida) e na crescente; para baixo na decrescente. */
  iconeOrdenacao(coluna: ColunaOrdenavel): string {
    return this.ariaSort(coluna) === 'descending' ? 'arrow-down' : 'arrow-up';
  }

  onFiltersChange(record: Record<string, string>): void {
    this.currentPage.set(1);

    const filtros: InstitutoFiltros = {};

    const nome = (record['nome'] ?? '').trim();
    if (nome) {
      filtros.nome = nome;
    }

    const matricula = (record['matricula'] ?? '').trim();
    if (matricula) {
      filtros.matricula = matricula;
    }

    if (record['plano'] && record['plano'] !== 'todos') {
      filtros.plano = Number(record['plano']);
    }

    if (record['idTipoSolicitacao'] && record['idTipoSolicitacao'] !== 'todos') {
      filtros.idTipoSolicitacao = Number(record['idTipoSolicitacao']);
    }

    if (record['statusUnificado'] && record['statusUnificado'] !== 'todos') {
      filtros.statusUnificado = record['statusUnificado'];
    }

    if (record['periodoInicial']) {
      filtros.periodoInicial = record['periodoInicial'];
    }

    if (record['periodoFinal']) {
      filtros.periodoFinal = record['periodoFinal'];
    }

    this.listarSolicitacoes(filtros);
  }

  /** Opção escolhida no menu ⋮ da linha; "Visualizar" abre o detalhe da solicitação. */
  onAcao(solicitacao: InstitutoPrevidenciario, opcao: string): void {
    if (opcao === 'visualizar') {
      void this.router.navigate(['/instituto/detalhe', solicitacao.idSolicitacao], {
        queryParams: { modo: 'visualizar' },
      });
    }
  }

  private listarSolicitacoes(filtros: InstitutoFiltros): void {
    this.loaderService.showLoader();

    this.institutoPrevidenciarioService
      .listarSolicitacoes(filtros)
      .pipe(finalize(() => this.loaderService.hideLoader()))
      .subscribe({
        next: ({ solicitacoes, ...totais }) => {
          this.solicitacoesSignal.set(solicitacoes);
          this.totaisSignal.set(totais);
        },
        error: (error: unknown) => {
          this.solicitacoesSignal.set([]);
          this.totaisSignal.set(TOTAIS_VAZIOS);
          const message =
            error instanceof ApiHttpError
              ? error.message
              : 'Não foi possível carregar as solicitações.';
          this.toastService.show(message, 'error');
        },
      });
  }
}
